import React from "react";
import PropTypes from "prop-types";
import { Redirect, Route } from "react-router-dom";
import { useSessionStore } from "../stores/sessionStore";
import { useRbac, NoPermissionState, PERM_MODULES } from "../components/DCKeeper";

/**
 * 受保護路由:兩道閘門。
 *
 * 1. **未登入** → 導回登入頁(既有行為)。
 * 2. **沒有該模組的權限** → 顯示無權限畫面,且**該頁元件完全不掛載**
 *    (票 route-guard/01)。第二點是重點:只把畫面遮起來的話,元件仍會 render、
 *    查詢照樣送出去 —— 那不叫擋住。
 *
 * 路由宣告的是 **`perm`(需要什麼權限)不是角色名**。角色是後端資料,
 * 自訂角色隨時會增加;寫死角色名的話,新建一個角色就什麼都進不去
 * (票 13 的翻版:`CAPABILITIES[role]` 對自訂角色回 undefined,`can()` 全 false)。
 *
 *   <AccessRoute path="/dck/monitor" perm="monitor.read" component={Monitor} />
 *
 * 沒宣告 `perm` 的路由維持原行為(只擋未登入)—— 例如登入後的共用頁。
 */
/**
 * perm → 模組名(給無權限畫面顯示)。從 PERM_MODULES 推導,不在路由表另寫一份 ——
 * 模組改名時只有一個地方要改(8/13 code review:Shotgun Surgery / Data Clumps)。
 */
const moduleLabelOf = (perm) => {
  if (!perm) return undefined;
  const res = perm.split(".")[0];
  return (PERM_MODULES.find((m) => m.resources.includes(res)) || {}).label;
};

const AccessRoute = ({ component: Component, perm, ...rest }) => {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const { can, hasNoResolvedPermissions, allModulesHidden } = useRbac();
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated) {
          return <Redirect to={{ pathname: "/auth-login", state: { from: props.location } }} />;
        }
        // 先判權限再決定要不要建立元件 —— 這一行的位置就是「有沒有真的擋住」
        if (perm && !can(perm)) {
          return (
            <NoPermissionState
              moduleLabel={moduleLabelOf(perm)}
              // 三種都擋,但要講對是哪一種 —— 每一種該找的人不同:
              // 這個模組沒開給你(找帳號管理者)／角色全部隱藏(找管理者去開權限)／
              // 一項都沒解析出來(對照表沒對上,找工程師)。
              misconfigured={hasNoResolvedPermissions}
              allHidden={allModulesHidden}
            />
          );
        }
        return <Component {...props} />;
      }}
    />
  );
};
AccessRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  /** 需要的權限,如 "monitor.read";不給=只擋未登入。模組名由 perm 推導 */
  perm: PropTypes.string,
};

export { AccessRoute };
